import { Injectable, computed, signal } from '@angular/core';
import { UploadItem } from '../interfaces/components/upload-item.interface';

const FINISHED_ITEM_LINGER_MS = 6000;

/** Tracks in-flight file uploads so they can run in the background and be shown in the uploads tray. */
@Injectable({ providedIn: 'root' })
export class UploadsService {
  private readonly uploadsState = signal<UploadItem[]>([]);

  readonly uploads = this.uploadsState.asReadonly();
  readonly hasActiveUploads = computed(() => this.uploadsState().some((u) => u.status === 'uploading'));

  start(id: string, fileName: string, kind: UploadItem['kind'], totalBytes: number): void {
    const item: UploadItem = { id, fileName, kind, totalBytes, loadedBytes: 0, status: 'uploading' };
    this.uploadsState.update((current) => [...current.filter((u) => u.id !== id), item]);
  }

  progress(id: string, loadedBytes: number): void {
    this.update(id, { loadedBytes });
  }

  finish(id: string, succeeded: boolean): void {
    this.uploadsState.update((current) =>
      current.map((u) =>
        u.id === id
          ? { ...u, status: succeeded ? 'completed' : 'failed', loadedBytes: succeeded ? u.totalBytes : u.loadedBytes }
          : u
      )
    );
    window.setTimeout(() => this.dismiss(id), FINISHED_ITEM_LINGER_MS);
  }

  dismiss(id: string): void {
    this.uploadsState.update((current) => current.filter((u) => u.id !== id));
  }

  private update(id: string, patch: Partial<UploadItem>): void {
    this.uploadsState.update((current) => current.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }
}
